import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StorePerformanceInterval } from '../../../../../../constants';
import { QueueLengthPerformanceItem } from '../../../../../../hooks';
import { Icons, Typography } from '../../../../../../stories/atoms';
import { ColumnFlexContainer, RowFlexContainer } from '../../../../../../stories/atoms/containerV2/ContainerV2';
import { IconWithText } from '../../../../../../stories/molecules';
import { homeTranslations } from '../../../../../../translations';
import { TillAggregateData } from '../gridView/GridView';
import { getAveragePercent } from '../util';
import { TillType } from './MobileGrid';
import { TillBreakdown } from './TillBreakdown';

type TillContainerProps = {
  isOpen: boolean;
  tillType: TillType;
  interval: StorePerformanceInterval;
  performanceItems: QueueLengthPerformanceItem[];
  aggregateData: TillAggregateData[];
  handleOpenTillType: (tillType: TillType) => void;
};

const { queueLength: queueLengthTranslations } = homeTranslations;

export const TillContainer: FC<TillContainerProps> = ({
  isOpen,
  tillType,
  interval,
  aggregateData,
  performanceItems,
  handleOpenTillType,
}) => {
  const { t } = useTranslation();

  const [expandedTills, setExpandedTills] = useState<Record<string, boolean>>({});

  const toggleTill = (tillId: string) => {
    setExpandedTills((prev) => ({
      ...prev,
      [tillId]: !prev[tillId],
    }));
  };

  const getTranslation = (till: TillType) => {
    if (till === 'DEFAULT') {
      return t(queueLengthTranslations.tabDefault.key, {
        defaultValue: queueLengthTranslations.tabDefault.defaultValue,
      });
    }
    return t(queueLengthTranslations.tabSelfCheckout.key, {
      defaultValue: queueLengthTranslations.tabSelfCheckout.defaultValue,
    });
  };

  return (
    <ColumnFlexContainer hasBorder borderRadius={[4]} padding={[3, 4]} gap={[4]} data-testid="till-type-container">
      <RowFlexContainer
        justifyContent="between"
        alignItems="center"
        cursor="pointer"
        data-testid="till-type-header-container"
        onClick={() => handleOpenTillType(tillType)}
      >
        <RowFlexContainer gap={[1]} style={{ gap: '4px' }}>
          <IconWithText
            iconOptions={{
              icon: Icons.DROPDOWN,
              size: 'small',
              rotate: isOpen ? 'custom' : 'right',
              rotateValue: 0,
            }}
          />
          <Typography type="h4">{getTranslation(tillType)}</Typography>
        </RowFlexContainer>
        <Typography type="h4">{getAveragePercent(aggregateData).toFixed(0)}%</Typography>
      </RowFlexContainer>
      {isOpen && !aggregateData.length && (
        <Typography>
          {t(queueLengthTranslations.noTills.key, queueLengthTranslations.noTills.defaultValue)}
        </Typography>
      )}
      {isOpen && !!aggregateData.length && (
        <TillBreakdown
          performanceItems={performanceItems}
          aggregateData={aggregateData}
          interval={interval}
          tillType={tillType}
          expandedTills={expandedTills}
          toggleTill={(till) => toggleTill(till)}
        />
      )}
    </ColumnFlexContainer>
  );
};
